import React, { useRef } from 'react';
import { Button, Form, Header, Icon, Label, List, Message, Segment, Statistic, Table } from 'semantic-ui-react';
import { DUPLICATE_OPTIONS } from 'pages/DataMapping/DYLFieldsConstants';
import { ACCEPTED_IMPORT_TYPES, CONFIDENCE_LABELS, IMPORT_MODULE_OPTIONS } from './importUtils';

const DataCheck = ({ analysis, isAnalyzing }) => (
    <Segment secondary loading={isAnalyzing}>
        <Header as='h5'>Data Check</Header>
        <Statistic.Group size='mini' widths={4}>
            <Statistic label='Rows' value={analysis.total_rows} />
            <Statistic color={analysis.duplicate_row_count ? 'orange' : undefined} label='Possible Duplicates' value={analysis.duplicate_row_count} />
            <Statistic color={analysis.invalid_emails ? 'orange' : undefined} label='Invalid Emails' value={analysis.invalid_emails} />
            <Statistic color={analysis.invalid_phones ? 'orange' : undefined} label='Invalid Phones' value={analysis.invalid_phones} />
        </Statistic.Group>
        {analysis.duplicate_groups.length > 0 && (
            <List bulleted className='Onboarding__duplicates'>
                {analysis.duplicate_groups.slice(0, 5).map(({ rows, label, reason }) => (
                    <List.Item key={rows.join('-')}>
                        <b>{label}</b>: rows {rows.join(', ')} ({reason.toLowerCase()})
                    </List.Item>
                ))}
                {analysis.duplicate_group_count > 5 && <List.Item>…and {analysis.duplicate_group_count - 5} more</List.Item>}
            </List>
        )}
        {analysis.missing_names > 0 && <p className='Onboarding__hint'>{analysis.missing_names} row(s) have no name and will import without one.</p>}
        <p className='Onboarding__hint'>This checks for duplicates within your file. Matches with records already in DYL are checked when the import runs.</p>
    </Segment>
);

const ImportData = ({
    importData,
    hasRows,
    fieldOptions,
    errors,
    isReadingFile,
    isMapping,
    isAnalyzing,
    onChange,
    onSelectFile,
    onUseSample,
    onChangeMapping,
    canContinue,
    onBack,
    onContinue
}) => {
    const fileInput = useRef(null);
    const { mode, file_name, headers = [], sample_row = [], mappings = [], module, module_reason, duplicate_option, analysis } = importData;
    const usedFields = mappings.map(({ dyl_field }) => dyl_field).filter(Boolean);

    return (
        <Segment>
            <Header as='h3'>
                Import Your Existing Data
                <Header.Subheader>Bring in contacts from a spreadsheet or another CRM export. We'll match the columns and look for duplicates.</Header.Subheader>
            </Header>
            <Form>
                <Form.Group inline>
                    <Form.Radio label='Import a file now' checked={mode === 'file'} onChange={() => onChange({ mode: 'file' })} />
                    <Form.Radio label="I'll import later, or have nothing to import" checked={mode === 'none'} onChange={() => onChange({ mode: 'none' })} />
                </Form.Group>
            </Form>

            {mode === 'none' && (
                <Message info size='small'>You can import anytime from Settings → Integrations → Import Data.</Message>
            )}

            {mode === 'file' && (
                <>
                    <div className='Onboarding__file-picker'>
                        <input
                            ref={fileInput}
                            type='file'
                            accept={ACCEPTED_IMPORT_TYPES}
                            style={{ display: 'none' }}
                            onChange={(e) => { onSelectFile(e.target.files[0]); e.target.value = ''; }}
                        />
                        <Button type='button' primary basic loading={isReadingFile} disabled={isReadingFile} onClick={() => fileInput.current.click()}>
                            <Icon name='file excel outline' /> {file_name ? 'Choose a Different File' : 'Choose CSV or Excel File'}
                        </Button>
                        <Button type='button' basic onClick={onUseSample} disabled={isReadingFile}>Try a Sample File</Button>
                        {file_name && <span><Icon name='file alternate outline' />{file_name}{importData.row_count !== undefined && ` · ${importData.row_count} rows`}</span>}
                    </div>
                    {errors.file && <Message warning size='small'>{errors.file}</Message>}
                    {file_name && !hasRows && (
                        <Message info size='small'>Your column matching was saved. Choose {file_name} again to re-check it for duplicates.</Message>
                    )}

                    {headers.length > 0 && (
                        <Segment basic style={{ padding: 0 }} loading={isMapping}>
                            <Header as='h4'>
                                Match Columns to DYL Fields
                                <Header.Subheader>We matched your columns automatically. Check the ones marked "Check" or "No match".</Header.Subheader>
                            </Header>
                            <Table compact unstackable className='Onboarding__mapping'>
                                <Table.Header>
                                    <Table.Row>
                                        <Table.HeaderCell>Your Column</Table.HeaderCell>
                                        <Table.HeaderCell>Example</Table.HeaderCell>
                                        <Table.HeaderCell>DYL Field</Table.HeaderCell>
                                        <Table.HeaderCell>Match</Table.HeaderCell>
                                    </Table.Row>
                                </Table.Header>
                                <Table.Body>
                                    {mappings.map(({ header, dyl_field, confidence, reason }, index) => {
                                        const confidenceLabel = CONFIDENCE_LABELS[dyl_field ? confidence : 'none'];
                                        const options = [
                                            { key: 'skip', value: '', text: "Don't import" },
                                            ...fieldOptions.map(option => ({ ...option, disabled: option.value !== dyl_field && usedFields.includes(option.value) }))
                                        ];
                                        return (
                                            <Table.Row key={`${header}-${index}`}>
                                                <Table.Cell><b>{header}</b></Table.Cell>
                                                <Table.Cell className='Onboarding__sample-value'>{String(sample_row[index] ?? '') || <i>empty</i>}</Table.Cell>
                                                <Table.Cell>
                                                    <Form.Select compact search value={dyl_field} options={options} onChange={(_, { value }) => onChangeMapping(index, value)} />
                                                </Table.Cell>
                                                <Table.Cell>
                                                    <Label size='tiny' basic color={confidenceLabel.color}>{confidenceLabel.text}</Label>
                                                    <div className='Onboarding__hint'>{reason}</div>
                                                </Table.Cell>
                                            </Table.Row>
                                        );
                                    })}
                                </Table.Body>
                            </Table>
                            {errors.mapping && <Message warning size='small'>{errors.mapping}</Message>}

                            <Form>
                                <Form.Group widths='equal'>
                                    <Form.Select
                                        label='Import As'
                                        value={module}
                                        options={IMPORT_MODULE_OPTIONS}
                                        onChange={(_, { value }) => onChange({ module: value })}
                                    />
                                    <Form.Select
                                        label='If Duplicates Are Found'
                                        value={duplicate_option}
                                        options={DUPLICATE_OPTIONS}
                                        onChange={(_, { value }) => onChange({ duplicate_option: value })}
                                    />
                                </Form.Group>
                            </Form>
                            {module_reason && <p className='Onboarding__hint'><Icon name='lightbulb outline' />{module_reason}</p>}
                            {analysis && <DataCheck analysis={analysis} isAnalyzing={isAnalyzing} />}
                        </Segment>
                    )}
                </>
            )}

            <div className='Onboarding__footer'>
                <Button basic type='button' onClick={onBack}><Icon name='arrow left' /> Back</Button>
                <Button primary floated='right' type='button' disabled={!canContinue} onClick={onContinue}>
                    Continue to Integrations <Icon name='arrow right' />
                </Button>
                <div style={{ clear: 'both' }} />
            </div>
        </Segment>
    );
}

export default ImportData;
